import React, { createContext, useContext, useState, useEffect } from 'react';
import { OnboardingTour } from './OnboardingTour';

interface OnboardingStep {
  id: string;
  title: string;
  content: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: () => void;
}

interface OnboardingContextType {
  startTour: (tourId: string) => void;
  completeTour: (tourId: string) => void;
  skipTour: (tourId: string) => void;
  isOnboardingVisible: boolean;
  hasCompletedTour: (tourId: string) => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

// Predefined tour configurations
export const TOUR_CONFIGS: Record<string, OnboardingStep[]> = {
  'parent-dashboard': [
    {
      id: 'welcome',
      title: 'Welcome to AppAcademia!',
      content: 'Let\'s take a quick tour to help you get started with tracking your child\'s academic progress.',
      target: '[data-tour="dashboard-header"]',
      position: 'bottom'
    },
    {
      id: 'child-overview',
      title: 'Child Overview',
      content: 'Here you can see an overview of your child\'s progress, recent activities, and achievements.',
      target: '[data-tour="child-overview"]',
      position: 'bottom'
    },
    {
      id: 'progress-charts',
      title: 'Progress Tracking',
      content: 'These charts show your child\'s learning progress over time across different subjects.',
      target: '[data-tour="progress-charts"]',
      position: 'top'
    },
    {
      id: 'ai-tutor',
      title: 'AI Tutor',
      content: 'Our AI tutor provides personalized learning recommendations and homework help.',
      target: '[data-tour="ai-tutor"]',
      position: 'left'
    },
    {
      id: 'exam-prep',
      title: 'Exam Preparation',
      content: 'Upload exam papers and get AI-powered practice questions to help your child prepare.',
      target: '[data-tour="exam-prep"]',
      position: 'left'
    }
  ],
  'teacher-dashboard': [
    {
      id: 'welcome',
      title: 'Welcome to AppAcademia!',
      content: 'Let\'s explore the tools available to enhance your teaching and track student progress.',
      target: '[data-tour="dashboard-header"]',
      position: 'bottom'
    },
    {
      id: 'class-overview',
      title: 'Class Overview',
      content: 'Monitor your students\' progress, attendance, and performance at a glance.',
      target: '[data-tour="class-overview"]',
      position: 'bottom'
    },
    {
      id: 'lesson-planner',
      title: 'Lesson Planner',
      content: 'Create and manage lessons with AI assistance for content generation.',
      target: '[data-tour="lesson-planner"]',
      position: 'right'
    },
    {
      id: 'assessment-tools',
      title: 'Assessment Tools',
      content: 'Create quizzes, assignments, and track student performance automatically.',
      target: '[data-tour="assessment-tools"]',
      position: 'left'
    },
    {
      id: 'analytics',
      title: 'Analytics Dashboard',
      content: 'Get insights into class performance and identify students who need extra support.',
      target: '[data-tour="analytics"]',
      position: 'top'
    }
  ],
  'student-dashboard': [
    {
      id: 'welcome',
      title: 'Welcome to Your Learning Hub!',
      content: 'This is your personal space to track progress, access lessons, and get AI tutoring.',
      target: '[data-tour="dashboard-header"]',
      position: 'bottom'
    },
    {
      id: 'current-lessons',
      title: 'Current Lessons',
      content: 'Continue where you left off or start new lessons assigned by your teacher.',
      target: '[data-tour="current-lessons"]',
      position: 'bottom'
    },
    {
      id: 'ai-chat',
      title: 'AI Study Buddy',
      content: 'Get instant help with homework and explanations for difficult concepts.',
      target: '[data-tour="ai-chat"]',
      position: 'left'
    },
    {
      id: 'progress-tracking',
      title: 'Your Progress',
      content: 'See how you\'re doing in different subjects and earn badges for achievements.',
      target: '[data-tour="progress-tracking"]',
      position: 'top'
    }
  ],
  'admin-dashboard': [
    {
      id: 'welcome',
      title: 'Welcome, Admin!',
      content: 'Here\'s your school management dashboard with system health and user activity.',
      target: '[data-tour="dashboard-header"]',
      position: 'bottom'
    },
    {
      id: 'user-stats',
      title: 'User Statistics',
      content: 'Monitor active teachers, students, and overall engagement.',
      target: '[data-tour="user-stats"]',
      position: 'bottom'
    },
    {
      id: 'system-health',
      title: 'System Health',
      content: 'Keep track of system performance, uptime, and resource usage.',
      target: '[data-tour="system-health"]',
      position: 'top'
    },
    {
      id: 'admin-actions',
      title: 'Quick Actions',
      content: 'Manage users, configure settings, and generate reports.',
      target: '[data-tour="admin-actions"]',
      position: 'left'
    }
  ]
};

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [currentTour, setCurrentTour] = useState<string | null>(null);
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Load completed tours from localStorage
    const saved = localStorage.getItem('onboarding-completed-tours');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCompletedTours(new Set(parsed));
      } catch (error) {
        console.error('[Onboarding] Failed to parse completed tours:', error);
      }
    }
  }, []);

  const saveCompletedTours = (tours: Set<string>) => {
    localStorage.setItem('onboarding-completed-tours', JSON.stringify([...tours]));
  };

  const startTour = (tourId: string) => {
    if (TOUR_CONFIGS[tourId] && !completedTours.has(tourId)) {
      setCurrentTour(tourId);
    }
  };

  const completeTour = (tourId: string) => {
    const newCompleted = new Set(completedTours);
    newCompleted.add(tourId);
    setCompletedTours(newCompleted);
    saveCompletedTours(newCompleted);
    setCurrentTour(null);
  };

  const skipTour = (tourId: string) => {
    const newCompleted = new Set(completedTours);
    newCompleted.add(tourId);
    setCompletedTours(newCompleted);
    saveCompletedTours(newCompleted);
    setCurrentTour(null);
  };

  const hasCompletedTour = (tourId: string) => {
    return completedTours.has(tourId);
  };

  const contextValue: OnboardingContextType = {
    startTour,
    completeTour,
    skipTour,
    isOnboardingVisible: currentTour !== null,
    hasCompletedTour
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
      {currentTour && TOUR_CONFIGS[currentTour] && (
        <OnboardingTour
          steps={TOUR_CONFIGS[currentTour]}
          isVisible={true}
          onComplete={() => completeTour(currentTour)}
          onSkip={() => skipTour(currentTour)}
        />
      )}
    </OnboardingContext.Provider>
  );
};
